import React, { Dispatch, SetStateAction, useEffect, useState } from "react"

import { gql, useQuery } from "urql"

import { ChevronLeftIcon } from "@heroicons/react/24/outline"

import { AlertLevel } from "~tailwindui/types/enums"

import { AlertMessageBox, WithLoadingIndicator } from "~tailwindui/Basics"

import commaList from "src/commaList"

import useIsMobile from "hooks/useIsMobile"

import LocalTime from "components/LocalTime"
import Avatar from "components/Messaging/Avatar"

import ManageUsersModal from "./ManageUsersModal"
import Message from "./Message"
import SendMessage from "./SendMessage"

import { MessageThread, User } from "."

export type OpenThreadProps = {
  viewer: User
  openThread: MessageThread
  setOpenThread: Dispatch<SetStateAction<MessageThread | null>>
}

const OpenThread: React.FC<OpenThreadProps> = ({
  viewer,
  openThread,
  setOpenThread,
}) => {
  const isMobile = useIsMobile(1024)
  const [loadAll, setLoadAll] = useState(false)
  const [result, fetchOldMessages] = useQuery({
    query: oldMessagesQuery,
    variables: { threadId: openThread.id },
    pause: true,
  })

  useEffect(() => {
    setLoadAll(false)
  }, [openThread.id])

  const otherUsers = openThread.users.filter(user => user.id !== viewer.id)

  return (
    <>
      <div className="flex h-full flex-col">
        <button
          type="button"
          onClick={() => setOpenThread(null)}
          className="mb-2 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-solid p-2 md:hidden"
          aria-label="Back to Threads"
        >
          <ChevronLeftIcon className="h-6 w-6 text-gray-500" />
          <span>Back to Threads</span>
        </button>
        <div className="border-b">
          <div className="flex items-center justify-between pb-3">
            <div className="text-left">
              <h2 className="text-xl">{openThread.title}</h2>
              <div className="text-sm text-gray-500">
                {commaList(otherUsers.map(u => u.messagingDisplayName))}
              </div>
            </div>
            {viewer.id === openThread.author.id ? (
              <ManageUsersModal
                messageThreadId={openThread.id}
                users={otherUsers}
              />
            ) : (
              <div className="flex -space-x-2 md:-space-x-3">
                {otherUsers.map(user => (
                  <div key={user.id}>
                    <Avatar
                      size={isMobile ? "small" : "medium"}
                      alt={user.messagingDisplayName}
                      src={user.avatar.thumbUrl}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          {openThread.users.filter(user => user.type === "Student").length >
            1 && (
            <AlertMessageBox level={AlertLevel.Info}>
              Students can see your messages, but they cannot see other
              students' messages.
            </AlertMessageBox>
          )}
        </div>
        <div className="flex min-h-0 flex-1 flex-col-reverse overflow-y-auto">
          {openThread.messages.data.map(message => (
            <Message viewer={viewer} key={message.id} message={message} />
          ))}

          {loadAll ? (
            <WithLoadingIndicator result={result}>
              {({ data: { viewer: v } }) => (
                <>
                  {v.messageThread.oldMessages.map(message => (
                    <Message
                      viewer={viewer}
                      key={message.id}
                      message={message}
                    />
                  ))}
                </>
              )}
            </WithLoadingIndicator>
          ) : openThread.messages.totalPages >
            openThread.messages.currentPage ? (
            <div className="my-4 flex justify-center">
              <button
                type="button"
                onClick={() => {
                  setLoadAll(true)
                  fetchOldMessages()
                }}
                className="flex max-w-max cursor-pointer items-center justify-center rounded-md border border-solid p-2 hover:bg-gray-100"
              >
                Load All Messages
              </button>
            </div>
          ) : null}

          <div className="mb-4 flex w-full items-center gap-4">
            <div className="hidden md:block">
              <Avatar
                src={openThread.author.avatar.thumbUrl}
                alt={openThread.author.messagingDisplayName}
              />
            </div>
            <div className="italic">
              Thread created by{" "}
              {openThread.author.id === viewer.id
                ? "you"
                : openThread.author.messagingDisplayName}{" "}
              on <LocalTime timestamp={openThread.createdAt} />
            </div>
          </div>
        </div>

        <div className="shrink-0 border-t pt-2">
          <SendMessage threadId={openThread.id} />
        </div>
      </div>
    </>
  )
}

const oldMessagesQuery = gql`
  query OldMessagesQuery($threadId: ID!) {
    viewer {
      id
      messageThread(id: $threadId) {
        id
        oldMessages {
          id
          read
          content
          createdAt
          contentFlagged
          author {
            id
            type
            firstName
            messagingDisplayName
            avatar {
              thumbUrl
            }
          }
          attachment {
            filename
            contentType
            downloadUrl
            url
            thumbUrl
            previewUrl
          }
        }
      }
    }
  }
`

export default OpenThread
